import {
    createScopeClauseFeedback,
    FeedbackType,
    IFeedbackCreate,
    IScopeClause,
    Vote,
} from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { FeedbackTypes } from "./FeedbackTypes";

interface IProps {
    /**
     * The scope clause to add feedback to
     */
    scopeClause: IScopeClause;
    /**
     * Vote of the feedback.
     */
    vote: Vote;
    /**
     * Callback function to call when the deletion is successful.
     */
    onSucceeded: () => void;
}

/**
 * Dialog that handles adding feedback to a scope clause.
 */
export const AddFeedbackDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const { scopeClause, vote, onSucceeded } = payload;

    const initialValues: IFeedbackCreate = {
        vote,
        feedback_type: FeedbackType.INCORRECT_CLAUSE_TYPE,
        additional_comments: "",
    };

    const { mutate, isPending, isSuccess } = useMutation({
        mutationFn: async (feedback: IFeedbackCreate) => {
            return createScopeClauseFeedback(
                scopeClause.scope_id,
                scopeClause.id,
                feedback
            );
        },
        onSuccess: () => {
            onSucceeded();
        },
    });

    const handleSuccess = (formData: IFeedbackCreate) => {
        mutate(formData);
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            sx={{ zIndex: 1000 }}
        >
            <FormContainer
                defaultValues={initialValues}
                onSuccess={handleSuccess}
            >
                <DialogContent sx={{ p: 4 }}>
                    <Stack alignItems={"center"} spacing={4} width={"100%"}>
                        <Stack alignItems={"center"} spacing={1}>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                                sx={{ color: "#54595E" }}
                            >
                                Feedback
                            </Typography>
                            {isPending && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign={"center"}
                                >
                                    Submitting Feedback
                                </Typography>
                            )}
                        </Stack>
                        {isPending && (
                            <CircularProgress sx={{ fontSize: 53 }} />
                        )}
                        {isSuccess && (
                            <>
                                <CheckCircleOutlineIcon sx={{ fontSize: 53 }} />
                                <Typography
                                    variant="caption"
                                    align="center"
                                    fontStyle={"italic"}
                                >
                                    Thank you. Your feedback has been submitted.
                                </Typography>
                            </>
                        )}
                        {!isSuccess && !isPending && (
                            <Paper
                                sx={{ p: 1.5, width: "100%" }}
                                variant="outlined"
                                square
                            >
                                <Stack gap={3}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Tell us more:
                                    </Typography>
                                    <FeedbackTypes />
                                    <FormTextFieldElement
                                        name="additional_comments"
                                        size="small"
                                        placeholder="(optional) Feel free to provide more details"
                                        multiline
                                    />
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{ px: 5, pb: 5, justifyContent: "center", gap: 3 }}
                >
                    {!isSuccess && (
                        <Button
                            variant="contained"
                            type="submit"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                bgcolor: "#000000",
                            }}
                            fullWidth
                            disabled={isPending}
                            disableElevation
                        >
                            Submit
                        </Button>
                    )}
                    {isSuccess && (
                        <Button
                            variant="contained"
                            type="button"
                            onClick={handleClose}
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                bgcolor: "#000000",
                            }}
                            fullWidth
                            disableElevation
                        >
                            Close
                        </Button>
                    )}
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
